import cn from 'clsx'
import Clickable from 'components/Common/Clickable'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import BodyText from '../Typography/BodyText'

const Chip = styled(Clickable)`
  display: inline-flex;
  align-items: center;
  border-radius: ${props => props.theme.borderRadius.round};
  position: relative;
  z-index: 0;
  transition: background-color 0.2s, color 0.2s, box-shadow 0.2s, border-color 0.2s;
  gap: ${rem(8)};
  white-space: nowrap;

  &:disabled {
    > * {
      pointer-events: none;
    }
  }

  &.icon-only {
    justify-content: center;
  }

  &.size-small {
    height: ${rem(28)};
    padding: 0 ${rem(8)};

    &.icon-only {
      padding: 0;
      width: ${rem(28)};
    }
  }

  &.size-medium {
    height: ${rem(32)};
    padding: 0 ${rem(12)};

    &.icon-only {
      padding: 0;
      width: ${rem(32)};
    }
  }

  &.size-large {
    height: ${rem(36)};
    padding: 0 ${rem(12)};

    &.icon-only {
      padding: 0;
      width: ${rem(36)};
    }
  }

  &.variant-outline {
    background-color: transparent;
    color: ${props => props.theme.palette.neutral.one};
    box-shadow: inset 0 0 0 1px ${props => props.theme.palette.neutral.five};

    &:not(:disabled) {
      &:hover {
        background-color: ${props => props.theme.palette.neutral.eight};
        box-shadow: inset 0 0 0 1px ${props => props.theme.palette.neutral.two};
      }

      &.error {
        box-shadow: inset 0 0 0 1px ${props => props.theme.palette.messaging.critical.main};
      }

      &:active {
        box-shadow: inset 0 0 0 2px ${props => props.theme.palette.neutral.one};
      }

      .input-keyboard &:focus {
        outline: 2px solid  ${props => props.theme.palette.neutral.five};
        outline-offset: 2px;
      }
    }

    &:disabled {
      color: ${props => props.theme.palette.neutral.four};
    }
  }

  &.variant-filled {
    background-color: ${props => props.theme.palette.neutral.eight};
    color: ${props => props.theme.palette.neutral.one};
    border: 1px solid ${props => props.theme.palette.neutral.five};

    &:not(:disabled) {
      &:hover {
        border: 1px solid ${props => props.theme.palette.neutral.two};
      }

      &.error {
        border-color: ${props => props.theme.palette.messaging.critical.main};
      }

      &:active {
        background-color: ${props => props.theme.palette.neutral.one};
        border-color: ${props => props.theme.palette.neutral.one};
        color: ${props => props.theme.palette.neutral.eight};
      }

      .input-keyboard &:focus {
        outline: 2px solid  ${props => props.theme.palette.neutral.five};
        outline-offset: 2px;
      }
    }

    &:disabled {
      background-color: ${props => props.theme.palette.neutral.four};
      color: ${props => props.theme.palette.neutral.eight};
    }
  }

  > svg, img {
    height: ${rem(16)};
    width: ${rem(16)};
  }
`

interface Props extends React.ComponentProps<typeof Clickable> {
  /** Determines the core visual representation of the chip */
  variant: 'filled' | 'outline';
  /** Determines factors like the height/padding of the chip */
  size: 'small' | 'medium' | 'large';
  /** Whether or not the chip should show itself visually in an error state */
  error?: boolean;
  /** An icon element that will go at the start (often the 'left' side) of the chip */
  startIcon?: React.ReactElement;
  /** An icon element that will go at the end (often the 'right' side) of the chip */
  endIcon?: React.ReactElement;
}

const AssistChip = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, Props>((props, ref) => {
  const { children, className, variant, size, error, startIcon, endIcon, ...buttonProps } = props

  const iconOnly = React.Children.toArray(children).filter(Boolean).length === 0 && ((startIcon && !endIcon) || (endIcon && !startIcon))

  return (<Chip
    {...buttonProps}
    ref={ref}
    className={cn(
      className,
      `variant-${variant}`,
      `size-${size}`,
      iconOnly ? 'icon-only' : undefined,
      { error },
    )}
  >
    {startIcon}
    {!iconOnly && <BodyText variant={size}>{children}</BodyText>}
    {endIcon}
  </Chip>)
})

AssistChip.displayName = 'AssistChip'

export default AssistChip
